import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'react-toastify';
import authContext from '../../../../AuthContext/authContext';
import classes from './PPOConfiguration.module.css';

const base_url = process.env.REACT_APP_BASE_URL;

const AddPPOConfiguration = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const ctx = useContext(authContext);
  const token = ctx.token;
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const { companyName } = useLocation().state || {};
  
  // Fetch company data to get expiry date
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await axios.get(`${base_url}/company/details/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setCompanyData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching company data:', error);
        toast.error('Failed to load company data');
        setLoading(false);
      }
    };
    
    fetchCompanyData();
  }, [companyId, token]);
  
  const validationSchema = Yup.object({
    ppoNote: Yup.string().required('Notes are required'),
    ppoPassCount: Yup.number()
      .required('Pass count is required')
      .min(1, 'At least 1 pass is required')
      .integer('Must be a whole number'),
    expiryDate: Yup.date()
      .required('Expiry date is required')
      .min(new Date(), 'Expiry date must be in the future'),
    isActive: Yup.boolean()
  });
  console.log(companyData?.subscriptionEndDate,"companyData?.subscriptionEndDate");
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initialValues = {
    ppoNote: '',
    ppoPassCount: 0,
    expiryDate: companyData?.subscriptionEndDate ? formatDateForInput(companyData.subscriptionEndDate) : formatDateForInput(new Date()),
    isActive: true
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      
      const ppoData = {
        companyId: companyId,
        ppoNote: values.ppoNote,
        ppoPassCount: values.ppoPassCount,
        ppoExpiryDate: values.expiryDate,
      };
      
      await axios.post(
        `${base_url}/company/create-ppo-subscription/${companyId}`,
        ppoData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('PPU configuration added successfully');
      navigate(`/company/${companyId}/ppo-configuration`, {
        state: {
          companyName: companyName,
        },
      });
    } catch (error) {
      console.error('Error saving PPO configuration:', error);
      toast.error(error.response?.data?.message || 'Failed to save PPO configuration');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={classes.container}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className={classes.form}>
            <div className={classes.header}>
              <h2>Add PPU Configuration</h2>
              {companyName && <h3>{companyName}</h3>}
            </div>
            <div className={classes.formGroup}>
              <label htmlFor="ppoNote">Notes</label>
              <Field
                as="textarea"
                id="ppoNote"
                name="ppoNote"
                className={classes.textarea}
                placeholder="Enter notes about this PPO configuration"
              />
              <ErrorMessage name="ppoNote" component="div" className={classes.error} />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="ppoPassCount">Number of Passes</label>
              <Field
                type="number"
                id="ppoPassCount"
                name="ppoPassCount"
                min="1"
                className={classes.input}
              />
              <ErrorMessage name="ppoPassCount" component="div" className={classes.error} />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="expiryDate">Expiry Date</label>
              <Field
                type="date"
                disabled={true}
                id="expiryDate"
                name="expiryDate"
                min={format(new Date(), 'yyyy-MM-dd')}
                className={classes.input}
              />
              <ErrorMessage name="expiryDate" component="div" className={classes.error} />
            </div>

            <div className={classes.buttonGroup}>
              <button
                type="button"
                className={classes.cancelButton}
                onClick={() => navigate(`/company/${companyId}`)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={classes.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save PPU Configuration'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddPPOConfiguration;
